import { useRef, useState, useEffect, useContext } from "react";
import { useSelector, useDispatch } from "react-redux";
import { useAuth0 } from "@auth0/auth0-react";
import { useReactFlow } from "reactflow";
import { Formik, Form } from "formik";
import FormField from "layouts/Flow Designer/components/FormField";
import TriggerFormWrapper from "../TriggerFormWrapper";
import FormTabs from "../FormTabs";
import {
  updateFlowTrigger,
  clearSelectedTrigger
} from "redux/actions/flowDesigner/triggersActionCreator";
import { openDiscardDialog } from "redux/actions/flowDesigner/commonActionCreator";
import { updateContextTrigger } from "redux/actions/flowDesigner/editorContextActionCreator";
import { TabContextApi } from "../../../../context";
import Comments from "layouts/Flow Designer/components/FlowPropertiesPanel/components/Comments";
import DiscardChangesDialog from "layouts/Flow Designer/components/DiscardChangesDialog";
import initialValues from "./schemas/initialValues";
import validations from "./schemas/validations";
import { form } from "./schemas/form";
import SoftBox from "components/SoftBox";
import SoftTypography from "components/SoftTypography";
import { getEnv } from "helpers/environment";

const TabPanel = ({ children, value = 0, index = 0 }) => {
  return <>{value === index && children}</>;
};

export const SocketIoTriggerForm = ({ allowEditWorkflow, handleGoBackToStartNodePanel }) => {
  const { user } = useAuth0();
  const { selectedTrigger, showTriggerPropertiesPanel } = useSelector(
    (store) => store?.designer?.triggers
  );
  const { currentSavedFlow } = useSelector((store) => store?.designer?.flows);
  const reactFlowInstance = useReactFlow();
  const [submitting, setSubmitting] = useState(false);
  const formRef = useRef(null);
  const [tabValue, setTabValue] = useState(0);
  const { autoSaveFlow } = useContext(TabContextApi);
  const dispatch = useDispatch();

  const {
    formId,
    formField: { notes, endpointUrl, rooms, success, failure }
  } = form;

  const handleChangeTab = (event, newValue) => {
    setTabValue(newValue);
  };

  const handleOpenDiscardDialog = () => {
    dispatch(openDiscardDialog());
  };

  const checkDirtyAndClose = () => {
    if (formRef.current.dirty) {
      handleOpenDiscardDialog();
      return;
    }
    closeForm();
  };

  const closeForm = () => {
    handleGoBackToStartNodePanel();
  };

  const updateStoreAndSaveFlow = (triggerProps) => {
    const currentTrigger = { ...selectedTrigger };
    currentTrigger.properties = { ...currentTrigger.properties, ...triggerProps };
    dispatch(updateContextTrigger(currentTrigger.label, currentSavedFlow?.name, triggerProps));
    dispatch(updateFlowTrigger(currentTrigger));
    dispatch(clearSelectedTrigger());
    autoSaveFlow(reactFlowInstance);
  };

  const handleSubmit = (event) => {
    event.preventDefault();
    formRef.current.submitForm();
  };

  const handleSaveSubmit = async (values, actions) => {
    const { rooms, notes } = values;

    setSubmitting(true);
    actions.setSubmitting(false);

    const triggerProps = {
      notes,
      rooms
    };

    updateStoreAndSaveFlow(triggerProps);

    setSubmitting(false);
    // closeForm();
  };

  const resetForm = () => {
    formRef.current?.resetForm();
  };

  const getInitialValues = () => {
    const selectedTriggerProps = selectedTrigger?.properties;

    return {
      ...initialValues,
      [endpointUrl.name]: `${getEnv("EXECUTOR_API_SOCKET")}/triggers?tenant_id=${
        user?.stripeId
      }&flow_id=${currentSavedFlow?.id}`,
      [success.name]: `triggers:success`,
      [failure.name]: `triggers:failure`,
      [rooms.name]: selectedTriggerProps?.rooms || [],
      [notes.name]: selectedTriggerProps?.notes || ""
    };
  };

  useEffect(() => {
    if (showTriggerPropertiesPanel) {
      setTabValue(0);

      return;
    }
    resetForm();
  }, [showTriggerPropertiesPanel]);

  return (
    <>
      <TriggerFormWrapper
        onSubmit={handleSubmit}
        onCancel={checkDirtyAndClose}
        submiting={submitting}
        tabValue={tabValue}
      >
        <Formik
          initialValues={getInitialValues()}
          onSubmit={handleSaveSubmit}
          validationSchema={validations}
          innerRef={formRef}
        >
          {({ values }) => {
            return (
              <Form id={formId}>
                <FormTabs tabValue={tabValue} onChange={handleChangeTab} />
                <TabPanel value={tabValue} index={0}>
                  <FormField
                    type={endpointUrl.type}
                    label={endpointUrl.label}
                    name={endpointUrl.name}
                    disabled={true}
                    readOnly={true}
                    value={values.endpointUrl}
                    multiline
                    rows={3}
                  />
                  <SoftBox>
                    <SoftTypography sx={{ fontSize: "14px" }}>
                      Use this events on your socket to listen for results
                    </SoftTypography>
                    <FormField
                      type={success.type}
                      label={success.label}
                      name={success.name}
                      disabled={true}
                      readOnly={true}
                      value={values.success}
                      multiline
                      rows={1}
                    />
                    <FormField
                      type={failure.type}
                      label={failure.label}
                      name={failure.name}
                      disabled={true}
                      readOnly={true}
                      value={values.failure}
                      multiline
                      rows={1}
                    />
                  </SoftBox>
                  <FormField
                    type={rooms.type}
                    label={rooms.label}
                    name={rooms.name}
                    placeholder={rooms.placeholder}
                    error={true}
                    value={values.rooms}
                    required
                    disabled={currentSavedFlow?.shared_status === "active" || !allowEditWorkflow}
                  />
                </TabPanel>
                <TabPanel value={tabValue} index={1}>
                  <FormField
                    type={notes.type}
                    label={notes.label}
                    name={notes.name}
                    value={values.notes}
                    multiline
                    rows={5}
                  />
                </TabPanel>
              </Form>
            );
          }}
        </Formik>
        <DiscardChangesDialog onAccept={closeForm} />
      </TriggerFormWrapper>
      <TabPanel value={tabValue} index={2}>
        <Comments tabValue={tabValue} node_id={selectedTrigger.type} />
      </TabPanel>
    </>
  );
};
